import PracticeStaffService, {
  type IPracticeStaffFilter,
  type TPracticeStaff,
  type TPracticeStaffMeRequest,
} from "@/api/models/practice/practiceStaff";
import type { TSelectFieldDynamicProps } from "@/types/selectField";
import type {
  TItemQueryComposableParams,
  TPaginatedQueryComposableParams,
} from "@/composables/shared/filters/useGeneralFilter";
import {
  splitComposableParams,
  resolveSelectEnabled,
} from "@/composables/shared/filters/useGeneralFilter";
import { createPathParamCrudMutations } from "@/composables/shared/mutations/useCrudMutations";
import { useGenericMutation } from "@/composables/shared/mutations/useGenericMutation";
import { ESubjects } from "@/api/models/auth/ability";
import {
  useGenericListQuery,
  useGenericPaginatedQuery,
  useGenericQuery,
  useGenericDetailQuery,
  createQueryKeys,
  type TItemQueryBehaviorOptions,
  type TOwnedFilterParams,
} from "@/composables/shared/queries";
import type { MaybeRefOrGetter } from "vue";

export const PracticeStaffQueryKeys = createQueryKeys(PracticeStaffService.queryKey, (key) => ({
  detail: key<string>("detail"),
  paginated: key<IPracticeStaffFilter>("paginated"),
  selected: key<IPracticeStaffFilter>("selected"),
}));

export const usePracticeStaffs = (
  filters?: MaybeRefOrGetter<Partial<IPracticeStaffFilter>>,
  enabled: MaybeRefOrGetter<boolean> = true
) => {
  return useGenericListQuery({
    queryKey: PracticeStaffQueryKeys.all(),
    params: { filters, enabled },
    fetcher: async (f) => {
      return await PracticeStaffService.getAll({}, f);
    },
    permission: $permSubjects.PRACTICE__PRACTICESTAFF,
  });
};

export const usePracticeStaffPaginated = (
  params: TOwnedFilterParams<IPracticeStaffFilter> = {}
) => {
  return useGenericPaginatedQuery<TPracticeStaff, Partial<IPracticeStaffFilter>>({
    params,
    queryKey: PracticeStaffQueryKeys.paginated(),
    fetcher: async (fetchParams) => {
      return await PracticeStaffService.getPaginatedFiltered(
        {},
        fetchParams.filters,
        fetchParams.pageParam
      );
    },
    permission: $permSubjects.PRACTICE__PRACTICESTAFF,
  });
};

export const usePracticeSelectedStaffs = (
  filters?: MaybeRefOrGetter<Partial<IPracticeStaffFilter>>,
  enabled: MaybeRefOrGetter<boolean> = true
) => {
  return useGenericListQuery<TPracticeStaff, Partial<IPracticeStaffFilter>>({
    queryKey: PracticeStaffQueryKeys.selected(),
    params: { filters, enabled },
    fetcher: async (f) => {
      return await PracticeStaffService.getAll({}, f);
    },
    permission: $permSubjects.PRACTICE__PRACTICESTAFF,
  });
};

export const usePracticeStaff = (
  params: TItemQueryComposableParams<string>,
  options: TItemQueryBehaviorOptions = {}
) => {
  return useGenericQuery<TPracticeStaff, string>({
    queryKey: PracticeStaffQueryKeys.detail(),
    params,
    fetcher: async (id) => {
      return await PracticeStaffService.get({ id }, { include_companies_data: true });
    },
    permission: $permSubjects.PRACTICE__PRACTICESTAFF,
    ...options,
    staleTime: options.staleTime ?? Infinity,
  });
};

export const {
  useCreate: useCreatePracticeStaff,
  useUpdate: useUpdatePracticeStaff,
  useDelete: useDeletePracticeStaff,
} = createPathParamCrudMutations<TPracticeStaff, { id?: string }>(PracticeStaffService, [], {
  entityName: "practice-staff",
  permissionSubject: ESubjects.PRACTICE__PRACTICESTAFF,
  invalidates: [
    PracticeStaffQueryKeys.all(),
    PracticeStaffQueryKeys.paginated(),
    PracticeStaffQueryKeys.selected(),
  ],
  removes: {
    delete: ({ id }) => [PracticeStaffQueryKeys.detail(id)],
  },
});

export const usePracticeStaffMe = () => {
  return useGenericDetailQuery<TPracticeStaff>({
    queryKey: PracticeStaffQueryKeys.detail("me"),
    fetcher: () => PracticeStaffService.me.get(),
    staleTime: Infinity,
  });
};

export const useUpdatePracticeStaffMe = () => {
  return useGenericMutation<TPracticeStaff, TPracticeStaffMeRequest>({
    mutationFn: (data) => PracticeStaffService.me.update(data),
    feature: "practiceStaff.me.update",
    invalidates: [PracticeStaffQueryKeys.detail("me")],
  });
};

export const usePracticeStaffForSelectField = (
  params: TPaginatedQueryComposableParams & Partial<IPracticeStaffFilter> = {
    enabled: true,
  }
): TSelectFieldDynamicProps<TPracticeStaff, IPracticeStaffFilter> => {
  const { filters } = splitComposableParams<IPracticeStaffFilter>(params);
  const query = usePracticeStaffPaginated({
    initial: {
      query: "",
      search: undefined,
      ...filters,
    },
    enabled: () => resolveSelectEnabled(params),
  });

  return {
    paginatedQuery: query,
    queryFilters: query.filters,
    filterField: "query",
    queryEnabled: query.queryEnabled,
  };
};
